import { Controller, FormProvider } from 'react-hook-form';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import styles from '../swgw-scanning/ProductNumber.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Tooltip } from '@dsi/react-eds';
import { useState, useEffect } from 'react';
import EditImagePathTagDialog from './EditImagePathTagDialog';
import DeleteImagePathTagDialog from './DeleteImagePathTagDialog';
import { useImagePathTagForm, imagePathTagDefaultValues } from './use-image-path-tag-form';
import ImagePathTagTable from './ImagePathTagTable';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useLoader } from '../../../hooks/useLoader';

interface ImagePathTagUIProps {
  repositoryId: string;
}

const ImagePathTagUI: React.FC<ImagePathTagUIProps> = ({ repositoryId }) => {
  const { addNotifications } = useSwobNotifications();
  const methods = useImagePathTagForm();
  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = methods;
  const [imagePathTags, setImagePathTags] = useState<{ image_path: string; tag: string; }[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [page, setPage] = useState(1);
  const [filterPath, setFilterPath] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<{ image_path: string; tag: string } | null>(null);
  const [deleteItem, setDeleteItem] = useState<{ image_path: string; tag: string } | null>(null);
  const { showLoader, hideLoader } = useLoader();

  const [sortAttr, setSortAttr] = useState<'image_path' | 'tag' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [pageSize, setPageSize] = useState('10');

  const imagePathTagData = async () => {
    try {
      showLoader();

      // Server-side pagination - fetch only current page
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize);

      const resp = await api.get(`/api/jfrog-config/${repositoryId}/image-path-tags?${params.toString()}`);
      setImagePathTags(resp.data.imagePathTags || []);
      setTotal(resp.data.total || 0);
      setTotalPages(resp.data.totalPages || 1);
      return resp;
    } catch (error: unknown) {
      setImagePathTags([]);
      setTotal(0);
      setTotalPages(1);
      addNotifications([
        {
          title: 'Error - Image Path & Tag',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching Image Path & Tag configurations',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  // Refetch when page or pageSize changes
  useQuery({
    queryKey: ['imagePathTags', repositoryId, page, pageSize],
    queryFn: imagePathTagData,
    retry: 0,
    refetchOnWindowFocus: false
  });

  // Client-side filtering on current page data
  const filteredImagePathTags = imagePathTags ? imagePathTags?.filter((item) => {
    const matchesPath = filterPath === '' || item.image_path.toLowerCase().includes(filterPath.toLowerCase());
    const matchesTag = filterTag === '' || item.tag.toLowerCase().includes(filterTag.toLowerCase());
    return matchesPath && matchesTag;
  }) : [];

  // Client-side sorting on current page data
  const sortedImagePathTags = [...filteredImagePathTags].sort((a, b) => {
    if (!sortAttr || !sortOrder) return 0;
    const aVal = a[sortAttr] || '';
    const bVal = b[sortAttr] || '';
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // No additional client-side pagination needed - already paginated from server
  const paginatedImagePathTags = sortedImagePathTags;

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const onSubmit = async (data: { image_path: string; tag: string }) => {
    const isDuplicate = imagePathTags.some((item) => item.image_path === data.image_path && item.tag === data.tag);
    if (isDuplicate) {
      addNotifications([
        {
          title: 'Warning - Image Path & Tag',
          icon: 'icon-triangle-warning',
          description: `Image path ${data.image_path} with tag ${data.tag} already exists.`,
          timestamp: new Date(),
          color: 'grey'
        }
      ]);
      return;
    }
    try {
      showLoader();

      const resp = await api.post(`/api/jfrog-config/${repositoryId}/image-path-tags`, {
        imagePathTags: [data]
      });
      reset(imagePathTagDefaultValues);
      imagePathTagData();

      if (resp.data.failed && resp.data.failed.length > 0) {
        addNotifications([
          {
            title: 'Error - Image Path & Tag',
            icon: 'icon-triangle-warning',
            description: `${resp.data?.failed[0]?.error}` || `Failed to add Image Path ${data.image_path}`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } else {
        addNotifications([
          {
            title: 'Success - Image Path & Tag',
            icon: 'icon-success',
            description: `Image path ${data.image_path} with tag ${data.tag} was added successfully.`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
      }

      return resp;
    } catch (error: unknown) {
      addNotifications([
        {
          title: 'Error - Image Path & Tag',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while adding Image Path & Tag',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      hideLoader();
    }
  };

  const handleEdit = (idx: number) => {
    setEditItem(paginatedImagePathTags[idx]);
    setEditDialogOpen(true);
  };

  const handleDelete = (idx: number) => {
    setDeleteItem(paginatedImagePathTags[idx]);
    setDeleteDialogOpen(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.settingsTitleContainer}>
        <div className={styles.settingsTitle}>Image Path & Tag Configuration</div>
        <p className={styles.productDescription}>Manage container image paths and tags from your JFrog Artifactory repository.</p>
      </div>
      <div className="row">
        <div className={styles.addProduct}>
          <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className={`${styles.settingsTitle} ${styles.whiteText}`}>Add Image Path & Tag</div>
                <div className={styles.formRow}>
                  <div>
                    <label htmlFor="image-path" className={styles.whiteText}>
                      Image Path
                      <span className={styles.requiredAsterisk}> *</span>{' '}
                      <ReactEDSWrapper component={Tooltip} message="The path to the container image in your JFrog Artifactory repository." pointer>
                        <span>
                          <i className={`icon icon-info ${styles.infoIcon}`}></i>
                        </span>
                      </ReactEDSWrapper>
                    </label>
                    <Controller
                      name="image_path"
                      control={control}
                      render={({ field }) => (
                        <ReactEDSWrapper
                          component={Input}
                          id="image-path"
                          type="text"
                          placeholder="e.g., my-project/my-image"
                          className={
                            (errors.image_path ? `${styles.inputField} ${styles.inputError}` : styles.inputField) + ' ' + styles.emailInputWide
                          }
                          {...field}
                        />
                      )}
                    />
                    <div className={styles.minHeight24}>
                      {errors.image_path ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {typeof errors.image_path.message === 'string' ? errors.image_path.message : 'Required'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="tag" className={styles.whiteText}>
                      Tag
                      <span className={styles.requiredAsterisk}> *</span>{' '}
                      <ReactEDSWrapper
                        component={Tooltip}
                        message="The image tag (version) in your JFrog Artifactory repository."
                        pointer
                      >
                        <span>
                          <i className={`icon icon-info ${styles.infoIcon}`}></i>
                        </span>
                      </ReactEDSWrapper>
                    </label>
                    <Controller
                      name="tag"
                      control={control}
                      render={({ field }) => (
                        <ReactEDSWrapper
                          component={Input}
                          id="tag"
                          type="text"
                          placeholder="e.g., latest, v1.0.0"
                          className={
                            (errors.tag ? `${styles.inputField} ${styles.inputError}` : styles.inputField) + ' ' + styles.emailInputWide
                          }
                          {...field}
                        />
                      )}
                    />
                    <div className={styles.minHeight24}>
                      {errors.tag ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {typeof errors.tag.message === 'string' ? errors.tag.message : 'Required'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.flexColStart}>
                    <label className={styles.invisibleLabel}>&nbsp;</label>
                    <ReactEDSWrapper component={Tooltip} message="Click to add the image path with the specified tag." pointer>
                      <span>
                        <ReactEDSWrapper component={Button} variant="secondary" className={`btn secondary ${styles.addButton}`} type="submit"
                          onClick={handleSubmit(onSubmit)}>
                          <i className="icon icon-plus" /> Add
                        </ReactEDSWrapper>
                      </span>
                    </ReactEDSWrapper>
                    <div className={styles.minHeight24}></div>
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        <div className={styles.vl2} ></div>
        <div className={styles.vl} >
          {total > 0 && (
            <ImagePathTagTable
              imagePathTags={paginatedImagePathTags}
              page={page}
              pageSize={pageSize}
              numPages={totalPages}
              filterPath={filterPath}
              filterTag={filterTag}
              sortAttr={sortAttr}
              sortOrder={sortOrder}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onSort={(order, attr) => {
                setSortAttr(attr);
                setSortOrder(order);
              }}
              onFilterPath={(val) => {
                setFilterPath(val);
                setPage(1);
              }}
              onFilterTag={(val) => {
                setFilterTag(val);
                setPage(1);
              }}
            />
          )}
        </div>
        <EditImagePathTagDialog
          repositoryId={repositoryId}
          open={editDialogOpen}
          initialItem={editItem}
          onSave={() => {
            setEditDialogOpen(false);
            setEditItem(null);
            imagePathTagData();
          }}
          onClose={() => {
            setEditDialogOpen(false);
            setEditItem(null);
          }}
        />
        <DeleteImagePathTagDialog
          repositoryId={repositoryId}
          open={deleteDialogOpen}
          item={deleteItem}
          onDelete={() => {
            setDeleteDialogOpen(false);
            setDeleteItem(null);
            imagePathTagData();
          }}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setDeleteItem(null);
          }}
        />
      </div>
    </div>
  );
}
export default ImagePathTagUI;
